import { gql } from 'apollo-boost';
import { enableExperimentalFragmentVariables } from 'graphql-tag';
import { FRAGMENT_BOOKING } from './booking-details/gql';

enableExperimentalFragmentVariables();

const FRAGMENT_ROOM_TYPE_AVAILABILITY_UNIT = gql`
  fragment fragmentRoomTypeAvailabilityUnit on RoomTypeAvailabilityUnit {
    numberOfAvailableBeds
    minPrice {
      minPriceForBillingCycle
    }
    roomType {
      id
      originId
      name
      category
      bathroomArrangement
      bathroomType
      kitchenArrangement
      kitchenType
      count
    }
    rollingRateValue
  }
`;

const FRAGMENT_EMAIL_RECORD = gql`
  fragment FragmentEmailRecord on EmailRecord {
    id
    templateId
    subject
    codeKey
    htmlContent
    status
    communicationWith
    to
    cc
    bcc
    editInfo {
      createdAt
      createdBy
      updatedAt
      updatedBy
    }
    originId
  }
`;

export const QUERY_NODES_ROOM_TYPES = gql`
  query QueryNodesRoomTypes($id: ID) {
    node(id: $id) {
      ... on Property {
        id
        roomTypes {
          id
          name
        }
      }
    }
  }
`;

export const QUERY_NODE_TENANCY_OPTION = gql`
  query QueryNodesTenancyOption(
    $id: ID
    $academicYearId: ID
    $bookingCategory: TenancyOptionBookingCategoryOption
    $isValid: Boolean
    $nominationPlanId: ID
  ) {
    node(id: $id) {
      ... on Property {
        id
        tenancyOptions(
          academicYearId: $academicYearId
          bookingCategory: $bookingCategory
          isValid: $isValid
          nominationPlanId: $nominationPlanId
        ) {
          id
          name
          originId
          description
          rateType
          tenancyType
          startDateType
          startDate
          endDateType
          endDate
          validFrom
          validTo
          tenancyLengthType
          tenancyLength
          status
          viewOnWebsite
          freeCancelInterval
          freeCancelBehaviorType
          freeCancelPointType
          intervalForTurnaround
          rateType
          chargingStartDate
          chargingEndDate
          isDifferentChargingDate
          tenancyLengthOption
          tenancyLengthOption
          bedGroups {
            id
            originId
            name
            type
          }
        }
      }
    }
  }
`;

export const QUERY_BOOKINGS = gql`
  query QueryBookings(
    $propertyId: ID!
    $filters: getBookingsFiltersInput!
    $pageNumber: Int = 1
    $pageSize: Int = 10
  ) {
    getBookings(
      propertyId: $propertyId
      filters: $filters
      pageNumber: $pageNumber
      pageSize: $pageSize
    ) {
      bookings {
        academicYear {
          id
          name
        }
        balanceInfo {
          processingBalance
        }
        bookingTips {
          type
          createdAt
        }
        onboardingEmailSent
        bookingType
        category
        countDown
        expiredReason
        heldToTime
        id
        originId
        partnerReferenceId
        onboardingEmailSent
        property {
          id
          timezone
        }
        isRebooker
        roomAllocation {
          bedId
          bedName
          buildingName
          roomName
          floorName
          flatName
        }
        roomType {
          name
          roomTypeId
        }
        rolloverReminderDate
        rolloverBookingCounts
        isRolling
        staffNotes {
          id
          content
          status
          type
          editInfo {
            createdAt
            createdBy
            updatedAt
            updatedBy
          }
        }
        status
        statusBeforeDisabled
        student {
          fullName
          gender
          email
          id
          generalInfo {
            basic {
              dateOfBirth
            }
          }
          contact {
            emergencyContacts {
              id
              contactEmail
            }
          }
        }
        tenancyOption {
          name
          tenancyOptionId
          startDate
          endDate
          length
        }
        bookingSpecialOffers {
          id
          specialOfferName
        }
        customFieldValues {
          customFieldType
          id
          value
        }
      }
      pageNumber
      pageSize
      total
      totalPage
    }
  }
`;

export const QUERY_BOOKINGS_WITH_GUARANTOR = gql`
  query QueryBookings(
    $propertyId: ID!
    $filters: getBookingsFiltersInput!
    $pageNumber: Int = 1
    $pageSize: Int = 10
  ) {
    getBookings(
      propertyId: $propertyId
      filters: $filters
      pageNumber: $pageNumber
      pageSize: $pageSize
    ) {
      bookings {
        id
        originId
        guarantors {
          id
          firstName
          middleName
          lastName
          title
          dateOfBirth
          relationship
          contactEmail
          mobilePhoneAreaCode
          mobilePhoneNumber
          homePhoneAreaCode
          homePhoneNumber
          countryTerritory
          countryTerritoryId
          cityTown
          cityTownId
          addressLine1
          addressLine2
          postcode
        }
        student {
          fullName
          gender
          email
          id
          preferredLanguage
          preferredLanguageFull
          contact {
            emergencyContacts {
              id
              contactEmail
            }
          }
        }
      }
      pageNumber
      pageSize
      total
      totalPage
    }
  }
`;

export const QUERY_ROOM_TYPE_AVAILABILITY_WITH_TENANCY_OPTION_OR_DATE = gql`
  query QueryRoomTypeAvailabilityWithTenancyOptionOrDate(
    $propertyId: ID!
    $filters: getRoomTypeAvailabilityWithTenancyOptionOrDateInput!
  ) {
    getRoomTypeAvailabilityWithTenancyOptionOrDate(propertyId: $propertyId, filters: $filters) {
      roomTypeInfos {
        ...fragmentRoomTypeAvailabilityUnit
      }
    }
  }
  ${FRAGMENT_ROOM_TYPE_AVAILABILITY_UNIT}
`;

export const QUERY_BOOKINGS_FOR_EXPORT = gql`
  query QueryBookings(
    $propertyId: ID!
    $filters: getBookingsFiltersInput!
    $pageSize: Int!
    $pageNumber: Int!
  ) {
    getBookings(
      propertyId: $propertyId
      filters: $filters
      pageNumber: $pageNumber
      pageSize: $pageSize
    ) {
      pageNumber
      pageSize
      total
      totalPage
      bookings {
        nominationPlan {
          name
          id
          originId
        }
        roomAllocation {
          roomName
          flatName
          floorName
          bedName
          buildingName
        }
        academicYear {
          id
          name
        }
        balanceInfo {
          currentBalance
          financialStatus
        }
        bedStatus {
          id
          nominationPlan {
            id
            name
          }
          status
          bed {
            id
          }
        }
        category
        checkInDate
        checkOutDate
        customFieldValues {
          labelName
          value
          customFieldType
        }
        editInfo {
          createdAt
          updatedAt
        }
        externalId
        heldToTime
        id
        isRebooker
        installment {
          name
        }

        originId
        partnerReferenceId
        priceForBillingCycle
        roomType {
          name
        }
        source
        status
        statusTime {
          bookingConfirmed
        }
        student {
          email
          externalId
          fullName
          id
          originId
          generalInfo {
            basic {
              dateOfBirth
            }
          }
          customFieldValues {
            labelName
            value
            customFieldType
          }
        }
        tenancyOption {
          name
          startDate
          endDate
          length
        }
        totalPrice
      }
    }
  }
`;

export const MUTATION_SELECT_BED = gql`
  mutation MutationSelectBed($propertyId: ID!, $input: selectBedInput!) {
    selectBed(propertyId: $propertyId, input: $input) {
      ...fragmentBookingDetailsBooking
    }
  }
  ${FRAGMENT_BOOKING}
`;

export const MUTATION_CREATE_BOOKING = gql`
  mutation MutationCreateBooking($propertyId: ID!, $input: createBookingInput!) {
    createBooking(propertyId: $propertyId, input: $input) {
      ...fragmentBookingDetailsBooking
    }
  }
  ${FRAGMENT_BOOKING}
`;

export const MUTATION_EDIT_BOOKING = gql`
  mutation MutationEditBooking($propertyId: ID!, $input: editBookingInput!) {
    editBooking(propertyId: $propertyId, input: $input) {
      ...fragmentBookingDetailsBooking
    }
  }
  ${FRAGMENT_BOOKING}
`;

export const MUTATION_EXPIRE_BOOKING = gql`
  mutation MutationExpireBooking($propertyId: ID!, $input: expireBookingInput!) {
    expireBooking(propertyId: $propertyId, input: $input) {
      ...fragmentBookingDetailsBooking
    }
  }
  ${FRAGMENT_BOOKING}
`;

export const MUTATION_REJECT_TAKEOVER = gql`
  mutation MutationRejectTakeover($propertyId: ID!, $input: rejectTakeoverInput!) {
    rejectTakeover(propertyId: $propertyId, input: $input) {
      ...fragmentBookingDetailsBooking
    }
  }
  ${FRAGMENT_BOOKING}
`;

export const QUERY_COMMUNICATION_RECORDS = gql`
  query QueryCommunicationRecords($propertyId: ID!, $filter: getCommunicationRecordsInput!) {
    getCommunicationRecords(propertyId: $propertyId, filter: $filter) {
      records {
        ...FragmentEmailRecord
      }
    }
  }
  ${FRAGMENT_EMAIL_RECORD}
`;

export const MUTATION_COMMUNICATION_RECORDS_RESEND_EMAIL = gql`
  mutation MutationCommunicationRecordsResendEmail($propertyId: ID!, $emailRecordId: ID!) {
    resendEmail(propertyId: $propertyId, emailRecordId: $emailRecordId) {
      ...FragmentEmailRecord
    }
  }
  ${FRAGMENT_EMAIL_RECORD}
`;

export const MUTATION_CREATE_OR_UPDATE_BOOKING_REFERRAL = gql`
  mutation MutationCreateOrUpdateBookingReferral($propertyId: ID!, $id: ID!, $referral: String) {
    createOrUpdateBookingReferral(propertyId: $propertyId, id: $id, referral: $referral) {
      ...fragmentBookingDetailsBooking
    }
  }
  ${FRAGMENT_BOOKING}
`;

export const MUTATION_CREATE_OR_REMOVE_CREDIT_RECORD = gql`
  mutation MutationCreateOrRemoveCreditRecord(
    $propertyId: ID!
    $input: creditBalanceOperationInput!
  ) {
    creditBalanceOperation(propertyId: $propertyId, input: $input) {
      id
      validAmount
      usedAmount
      remainingAmount
      clawbackAmount
    }
  }
`;
export const MUTATION_REVERSERE_DEMPTION = gql`
  mutation MutationReverseRedemption($propertyId: ID!, $input: reverseRedemptionInput!) {
    reverseRedemption(propertyId: $propertyId, input: $input) {
      id
      name
    }
  }
`;

export const QUERY_STUDENTS = gql`
  query QueryStudents(
    $propertyId: ID!
    $originIdLike: String
    $nameLike: String
    $roomLocation: getStudentsRoomLocationOptions = ALL
    $pageNumber: Int = 1
    $pageSize: Int = 5
  ) {
    getStudents(
      propertyId: $propertyId
      originIdLike: $originIdLike
      nameLike: $nameLike
      roomLocation: $roomLocation
      pageNumber: $pageNumber
      pageSize: $pageSize
    ) {
      students {
        fullName
        gender
        id
        originId
        externalId
        isRebooker
        account {
          emailVerified
        }
        profilePhotoFileUuid
        bookings(propertyId: $propertyId) {
          status
        }
        email
      }
    }
  }
`;

export const QUERY_GET_LISTING_BOOKINGS_FILTERS = gql`
  query getListingBookingsFilters($propertyId: ID!) {
    getListingBookingsFilters(propertyId: $propertyId) {
      tenancyOptionNames
      roomTypeNames
      bedNames
      roomNames
      flatNames
      floorNames
      buildingNames
    }
  }
`;

export const MUTATION_BULK_SEND_EMAIL = gql`
  mutation bulkSendBookingEmail($propertyId: ID!, $input: bulkSendBookingEmailInput!) {
    bulkSendBookingEmail(propertyId: $propertyId, input: $input) {
      success
    }
  }
`;

export const QUERY_GET_BULK_TASK = gql`
  query getBulkTask($propertyId: ID!, $filters: getBulkTaskFiltersInput!) {
    getBulkTask(propertyId: $propertyId, filters: $filters) {
      id
      callArgs
    }
  }
`;

export const QUERY_GET_RATE_TYPE = gql`
  query getRateType($propertyId: ID!, $academicYearId: ID!) {
    getFlexibilityControls(propertyId: $propertyId, academicYearId: $academicYearId) {
      setUpRateType {
        value
      }
    }
  }
`;

export const MUTATION_EXPORT_BOOKINGS = gql`
  mutation exportBookings($propertyId: ID!, $input: exportBookingsInput!) {
    exportBookings(propertyId: $propertyId, input: $input) {
      status
    }
  }
`;
